import { useEffect, useRef } from "react";
import { Bold, Italic, Heading2, Link2, List, Image } from "lucide-react";

// Small dependency-free rich text editor. Produces simple, email-safe HTML.
export const RichText = ({ value, onChange, testid }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) ref.current.innerHTML = value || "";
    // Only syncs when the campaign being edited changes, never on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testid]);

  const cmd = (command, arg) => {
    ref.current?.focus();
    document.execCommand(command, false, arg);
    onChange(ref.current.innerHTML);
  };

  const addLink = () => {
    const url = window.prompt("Link address (must start with https://)");
    if (!url) return;
    if (!url.startsWith("https://")) {
      window.alert("Email links must start with https://");
      return;
    }
    cmd("createLink", url);
  };

  const addImage = () => {
    const url = window.prompt("Image address (must start with https://)");
    if (!url) return;
    if (!url.startsWith("https://")) {
      window.alert("Email images must start with https://");
      return;
    }
    cmd("insertHTML", `<img src="${url}" alt="" style="max-width:100%;height:auto" />`);
  };

  const tools = [
    { icon: Bold, label: "Bold", run: () => cmd("bold") },
    { icon: Italic, label: "Italic", run: () => cmd("italic") },
    { icon: Heading2, label: "Heading", run: () => cmd("formatBlock", "<h2>") },
    { icon: List, label: "Bullet list", run: () => cmd("insertUnorderedList") },
    { icon: Link2, label: "Link", run: addLink },
    { icon: Image, label: "Image", run: addImage },
  ];

  return (
    <div className="border border-line bg-white">
      <div className="flex flex-wrap gap-1 border-b border-line px-2 py-1.5">
        {tools.map((t) => (
          <button
            key={t.label}
            type="button"
            onClick={t.run}
            title={t.label}
            aria-label={t.label}
            data-testid={`richtext-${t.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="h-8 w-8 flex items-center justify-center text-[#5A5A5A] hover:text-charcoal hover:bg-bone transition-colors"
          >
            <t.icon size={15} />
          </button>
        ))}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        data-testid={testid}
        className="min-h-[260px] px-4 py-3.5 text-[14.5px] font-light leading-relaxed text-charcoal focus:outline-none prose-email"
      />
    </div>
  );
};
